/**
 * COReviewWorkspace - TASK-374: CO Review System
 *
 * Main review layout with three sections:
 * - Left sidebar: section list with reviewed/unreviewed indicators
 * - Main content: COSectionViewer for the selected section (read-only KYC data)
 * - Bottom panel: SectionCommentPanel for the current section
 *
 * Fetches review data and KYC profile data from the API.
 * When all 10 sections are reviewed, the decision buttons are enabled.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Circle,
  ChevronLeft,
  Loader2,
  AlertTriangle,
  Gavel,
  FileCheck,
  Inbox,
  FileUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../../utils/logger';
import { useDockStore } from '../../../stores/dockStore';
import {
  getReview,
  markSectionReviewed,
  submitDecision,
  getComments,
} from '../../../services/reviewService';
import type {
  KYCReview,
  KYCReviewComment,
  KnowledgeGraphEntry,
  ReviewDecision,
  RejectionCategory,
} from '../../../types/complianceWorkflow';
import COSectionViewer from './COSectionViewer';
import DocumentRequestForm from './DocumentRequestForm';
import DocumentRequestList from './DocumentRequestList';
import SectionCommentPanel from './SectionCommentPanel';
import ReviewDecisionModal from './ReviewDecisionModal';

/** KYC sections to review, in order — must match backend review_service.py */
const KYC_SECTIONS = [
  { id: 'personalInfo', label: 'Personal Information' },
  { id: 'contactInfo', label: 'Contact Details' },
  { id: 'employmentInfo', label: 'Employment' },
  { id: 'personalBackground', label: 'Personal Background' },
  { id: 'taxInfo', label: 'Tax Information' },
  { id: 'adverseMedia', label: 'Adverse Media & PEP' },
  { id: 'businessActivities', label: 'Business Activities' },
  { id: 'wealthSources', label: 'Source of Wealth' },
  { id: 'incomeSources', label: 'Income & Assets' },
  { id: 'reviewSubmit', label: 'Review & Submit' },
];

/**
 * TASK-733: the workspace is host-agnostic. An explicit `reviewId` prop wins; without one
 * it falls back to the `/compliance/review/:reviewId` route param, which is what keeps the
 * deep link and refresh working. `subjectKey` is the persisted signal subject when the
 * review was opened from one — it participates in canvas tile identity (TASK-726) and is
 * never composed here. Post-decision behaviour belongs to the HOST: the route navigates
 * as before; the canvas refreshes in place and tells its surroundings via `onCompleted`.
 */
export interface COReviewWorkspaceProps {
  reviewId?: string;
  subjectKey?: string;
  host?: 'route' | 'canvas';
  onCompleted?: () => void;
}

/** TASK-733: how canvas surfaces hear that a decision landed (the queue refetches on it). */
export const REVIEW_DECIDED_EVENT = 'kielis:review-decided';

const COReviewWorkspace: React.FC<COReviewWorkspaceProps> = ({
  reviewId: reviewIdProp,
  host = 'route',
  onCompleted,
}) => {
  const routeParams = useParams<{ reviewId: string }>();
  const reviewId = reviewIdProp ?? routeParams.reviewId;
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [decisionAnnouncement, setDecisionAnnouncement] = useState<string | null>(null);
  const [review, setReview] = useState<KYCReview | null>(null);
  const [comments, setComments] = useState<KYCReviewComment[]>([]);
  const [kycData, setKycData] = useState<Record<string, unknown>>({});
  const [knowledgeGraph, setKnowledgeGraph] = useState<KnowledgeGraphEntry[]>([]);
  const [activeSectionId, setActiveSectionId] = useState(KYC_SECTIONS[0].id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingReviewed, setMarkingReviewed] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  // TASK-927: the CO asks for evidence where they review. `requestsRefreshKey` remounts
  // the list after a request is raised — DocumentRequestList fetches on mount by clientId
  // and exposes no imperative refetch, so the key is what makes a new row show up.
  const [showDocumentRequestForm, setShowDocumentRequestForm] = useState(false);
  const [requestsRefreshKey, setRequestsRefreshKey] = useState(0);

  const fetchReviewData = useCallback(async () => {
    if (!reviewId) return;

    try {
      setLoading(true);
      setError(null);

      const reviewData = await getReview(reviewId);
      setReview(reviewData);

      if (reviewData.comments) {
        setComments(reviewData.comments);
      }

      // KYC data is typically embedded in the review response or fetched separately.
      // We use what the API provides in the review payload.
      const data = reviewData as unknown as Record<string, unknown>;
      if (data.kyc_data) {
        setKycData(data.kyc_data as Record<string, unknown>);
      }
      if (data.knowledge_graph) {
        setKnowledgeGraph(data.knowledge_graph as KnowledgeGraphEntry[]);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load review';
      setError(message);
      logger.error('Failed to fetch review data:', err);
    } finally {
      setLoading(false);
    }
  }, [reviewId]);

  // TASK-694: the dock gets no client from a `/compliance/review/:reviewId` path — the
  // route names the review, not its subject. Supply it the way PrepChatPanel/KYCChatPanel do,
  // via the dock context override, so "is this file compliant" answers about the client
  // under review. Cleared on unmount so the override never outlives the workspace.
  useEffect(() => {
    if (!review?.client_id) return;
    useDockStore.getState().setContextOverride({
      entity: { type: 'client', id: review.client_id, name: review.client_name },
      page_data: { client_id: review.client_id },
    });
    return () => useDockStore.getState().setContextOverride(null);
  }, [review?.client_id, review?.client_name, reviewId]);

  const fetchComments = useCallback(async () => {
    if (!reviewId) return;

    try {
      const data = await getComments(reviewId);
      setComments(data);
    } catch (err) {
      logger.error('Failed to refresh comments:', err);
    }
  }, [reviewId]);

  useEffect(() => {
    fetchReviewData();
  }, [fetchReviewData]);

  // TASK-733 accessibility: focus moves to the opened workspace, so a keyboard user who
  // selected a review from the queue lands IN it rather than back at the top of the page.
  // Fires when loading finishes — the container does not exist during the spinner.
  useEffect(() => {
    if (!loading && review) containerRef.current?.focus();
  }, [loading, review, reviewId]);

  const sectionsReviewed = review?.sections_reviewed || [];
  const allSectionsReviewed = KYC_SECTIONS.every((s) =>
    sectionsReviewed.includes(s.id)
  );

  const handleMarkReviewed = async () => {
    if (!reviewId) return;

    try {
      setMarkingReviewed(true);
      await markSectionReviewed(reviewId, activeSectionId);
      toast.success(`Section "${KYC_SECTIONS.find((s) => s.id === activeSectionId)?.label}" marked as reviewed`);

      // Update local state
      setReview((prev) => {
        if (!prev) return prev;
        const updated = new Set([...prev.sections_reviewed, activeSectionId]);
        return {
          ...prev,
          sections_reviewed: Array.from(updated),
          all_sections_reviewed: KYC_SECTIONS.every((s) => updated.has(s.id)),
        };
      });

      // Auto-advance to next unreviewed section
      const currentIdx = KYC_SECTIONS.findIndex((s) => s.id === activeSectionId);
      const nextUnreviewed = KYC_SECTIONS.find(
        (s, idx) => idx > currentIdx && !sectionsReviewed.includes(s.id) && s.id !== activeSectionId
      );
      if (nextUnreviewed) {
        setActiveSectionId(nextUnreviewed.id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to mark section as reviewed';
      toast.error(message);
      logger.error('Failed to mark section reviewed:', err);
    } finally {
      setMarkingReviewed(false);
    }
  };

  const handleDecisionSubmit = async (
    decision: ReviewDecision,
    notes: string,
    category?: RejectionCategory
  ) => {
    if (!reviewId) return;

    try {
      await submitDecision(reviewId, {
        decision,
        notes,
        rejection_category: category,
      });

      const label =
        decision === 'approve'
          ? 'approved'
          : decision === 'reject'
          ? 'rejected'
          : 'changes requested';
      toast.success(`KYC review ${label} successfully`);
      setShowDecisionModal(false);
      setDecisionAnnouncement(`KYC review ${label}.`);
      // TASK-733: post-decision behaviour is the HOST's. The route keeps its existing
      // navigation; the canvas refreshes this tile in place (surrounding tiles stay
      // mounted) and broadcasts so the queue tile refetches.
      window.dispatchEvent(
        new CustomEvent(REVIEW_DECIDED_EVENT, { detail: { reviewId } }),
      );
      if (host === 'canvas') {
        onCompleted?.();
        await fetchReviewData();
      } else {
        navigate('/compliance');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit decision';
      toast.error(message);
      setDecisionAnnouncement(`Decision failed: ${message}`);
      logger.error('Failed to submit decision:', err);
    }
  };

  const isSectionReviewed = (sectionId: string) =>
    sectionsReviewed.includes(sectionId);

  const reviewedCount = KYC_SECTIONS.filter((s) =>
    isSectionReviewed(s.id)
  ).length;

  // TASK-733: no prop AND no route param — a recoverable empty state, and NO review API
  // call was made (fetchReviewData guards on reviewId). Never an error screen: an empty
  // tile the RM summoned deliberately is not a failure.
  if (!reviewId) {
    return (
      <div
        data-testid="review-workspace-empty"
        className="flex flex-col items-center justify-center py-16"
      >
        <Inbox className="w-8 h-8 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">No review selected</p>
        <p className="text-xs text-gray-400 mt-1">
          Pick a review from the queue to open it here.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
        <p className="text-sm text-gray-500">Loading review workspace...</p>
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertTriangle className="w-8 h-8 text-red-500 mb-3" />
        <p className="text-sm text-red-600 mb-2">{error || 'Review not found'}</p>
        {host === 'canvas' ? (
          // The canvas has no queue route to go back to — retry in place instead.
          <button
            onClick={fetchReviewData}
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Try again
          </button>
        ) : (
          <button
            onClick={() => navigate('/compliance')}
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Back to queue
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      data-testid="review-workspace"
      aria-label={`KYC review — ${review.client_name || 'client'}`}
      className="h-full flex flex-col outline-none"
    >
      {/* TASK-733 accessibility: the decision outcome is ANNOUNCED, not merely painted. */}
      <div aria-live="polite" className="sr-only" data-testid="review-decision-announcement">
        {decisionAnnouncement}
      </div>
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3">
          {host !== 'canvas' && (
          <button
            onClick={() => navigate('/compliance')}
            aria-label="Back to compliance"
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          )}
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {review.client_name || 'KYC Review'}
            </h2>
            <p className="text-xs text-gray-500">
              {reviewedCount}/{KYC_SECTIONS.length} sections reviewed
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Progress indicator */}
          <div className="flex items-center gap-1.5">
            {KYC_SECTIONS.map((section) => (
              <div
                key={section.id}
                className={`w-2 h-2 rounded-full ${
                  isSectionReviewed(section.id) ? 'bg-green-500' : 'bg-gray-300'
                }`}
                title={`${section.label}: ${isSectionReviewed(section.id) ? 'Reviewed' : 'Not reviewed'}`}
              />
            ))}
          </div>

          {/* Decision button */}
          <button
            onClick={() => setShowDecisionModal(true)}
            disabled={!allSectionsReviewed && reviewedCount === 0}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              allSectionsReviewed
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            <Gavel className="w-4 h-4" />
            Submit Decision
          </button>
        </div>
      </div>

      {/* Main layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar: section list */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto shrink-0">
          <div className="p-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Sections
            </h3>
            <nav className="space-y-1">
              {KYC_SECTIONS.map((section) => {
                const reviewed = isSectionReviewed(section.id);
                const isActive = activeSectionId === section.id;
                const sectionCommentCount = comments.filter(
                  (c) => c.section_id === section.id && !c.is_resolved
                ).length;

                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSectionId(section.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                      isActive
                        ? 'bg-white shadow-sm border border-gray-200 text-gray-900 font-medium'
                        : 'text-gray-600 hover:bg-white hover:text-gray-900'
                    }`}
                  >
                    {reviewed ? (
                      <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-gray-300 shrink-0" />
                    )}
                    <span className="flex-1 truncate">{section.label}</span>
                    {sectionCommentCount > 0 && (
                      <span className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs bg-yellow-100 text-yellow-700 font-medium">
                        {sectionCommentCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Right panel: section viewer + comments */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Section viewer */}
          <div className="flex-1 overflow-y-auto p-6">
            <COSectionViewer
              sectionId={activeSectionId}
              kycData={kycData}
              knowledgeGraph={knowledgeGraph}
            />

            {/* Mark as Reviewed button */}
            <div className="mt-6 flex items-center justify-end">
              {isSectionReviewed(activeSectionId) ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-lg border border-green-200">
                  <FileCheck className="w-4 h-4" />
                  Section Reviewed
                </div>
              ) : (
                <button
                  onClick={handleMarkReviewed}
                  disabled={markingReviewed}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {markingReviewed ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Mark as Reviewed
                </button>
              )}
            </div>
          </div>

          {/* Bottom panel: comments */}
          <div className="border-t border-gray-200 bg-gray-50 p-4 max-h-[40%] overflow-y-auto">
            <SectionCommentPanel
              reviewId={reviewId!}
              sectionId={activeSectionId}
              comments={comments}
              onCommentAdded={fetchComments}
            />
          </div>
        </div>

        {/* TASK-927: right rail — evidence the CO has asked the client for. Raised here it
            takes the same POST /api/document-requests path as anywhere else, so the row and
            its secure link land on the RM's checklist and the client's links tab unchanged. */}
        {review?.client_id && (
          <div className="w-80 border-l border-gray-200 bg-gray-50 overflow-y-auto shrink-0">
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Document Requests
                </h3>
                <button
                  onClick={() => setShowDocumentRequestForm(true)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FileUp className="w-3.5 h-3.5" />
                  Request
                </button>
              </div>
              <DocumentRequestList key={requestsRefreshKey} clientId={review.client_id} />
            </div>
          </div>
        )}
      </div>

      {/* Document request modal */}
      {review?.client_id && (
        <DocumentRequestForm
          isOpen={showDocumentRequestForm}
          onClose={() => setShowDocumentRequestForm(false)}
          clientId={review.client_id}
          onCreated={() => setRequestsRefreshKey((k) => k + 1)}
        />
      )}

      {/* Decision modal */}
      <ReviewDecisionModal
        isOpen={showDecisionModal}
        onClose={() => setShowDecisionModal(false)}
        onSubmit={handleDecisionSubmit}
        allSectionsReviewed={allSectionsReviewed}
      />
    </div>
  );
};

export default COReviewWorkspace;
